import { useState } from 'react';
import { Server } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
  Button,
  Badge,
  Skeleton,
  RadioGroup,
  RadioGroupItem,
} from '@vnetwork/ui/primitives';
import { EmptyState } from '@vnetwork/ui/data';
import { useToast } from '@vnetwork/ui/feedback';
import { useClusterIngressGateways, usePinIngressGateway, type IngressGateway } from '@/api/k8s';

const optionCard = (selected: boolean): React.CSSProperties => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: 'var(--vn-space-3)',
  padding: 'var(--vn-space-3)',
  borderRadius: 'var(--vn-radius-md)',
  border: `1px solid ${selected ? 'var(--vn-border-strong)' : 'var(--vn-border-subtle)'}`,
  background: selected ? 'var(--vn-bg-subtle)' : 'transparent',
  cursor: 'pointer',
});
const optTitle: React.CSSProperties = { fontWeight: 'var(--vn-font-weight-semibold)' as React.CSSProperties['fontWeight'], fontSize: 'var(--vn-font-size-sm)' };
const optMuted: React.CSSProperties = { color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' };

/**
 * Pick which ingress LB the console tracks. Each ingress controller exposed as a
 * Service of type LoadBalancer has its own OpenNebula load balancer VM; a cluster running
 * more than one controller (e.g. a self-managed ingress next to the managed nginx
 * add-on) has several. The picked LB drives the Statistics / Monitoring / Resize views.
 */
export function IngressGatewayPicker({ serviceId, clusterId, onClose }: {
  serviceId: string;
  clusterId: string;
  onClose: () => void;
}) {
  const { show } = useToast();
  const { data, isLoading } = useClusterIngressGateways(serviceId, clusterId);
  const gateways: IngressGateway[] = data?.gateways ?? [];
  const [sel, setSel] = useState<string>(data?.selected ?? gateways.find((g) => g.selected)?.id ?? '');

  const pin = usePinIngressGateway(serviceId, clusterId, {
    onSuccess: () => { show({ tone: 'success', title: 'Ingress gateway updated' }); onClose(); },
    onError: () => show({ tone: 'danger', title: 'Failed to update ingress gateway' }),
  });

  const submit = () => { if (sel) pin.mutate({ lb_id: sel }); };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent size="lg">
        <DialogHeader><DialogTitle>Select ingress gateway</DialogTitle></DialogHeader>
        <DialogBody>
          <p style={{ ...optMuted, marginTop: 0, marginBottom: 'var(--vn-space-3)' }}>
            Choose which ingress load balancer the console monitors. Each ingress controller
            exposed as a LoadBalancer service has its own load balancer — pick the one whose
            traffic and health you want to view.
          </p>

          {isLoading ? (
            <Skeleton style={{ height: 160 }} />
          ) : gateways.length === 0 ? (
            <EmptyState
              icon={<Server size={20} />}
              title="No ingress load balancers"
              description="This cluster has no ingress controller exposed as a LoadBalancer service yet. Deploy one (or enable the NGINX add-on) and it will appear here."
            />
          ) : (
            <RadioGroup value={sel} onValueChange={setSel}>
              <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
                {gateways.map((g) => (
                  <label key={g.id} style={optionCard(sel === g.id)}>
                    <RadioGroupItem value={g.id} />
                    <div style={{ display: 'grid', gap: 'var(--vn-space-1)', width: '100%' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)', flexWrap: 'wrap' }}>
                        <code style={optTitle}>{g.name}</code>
                        {g.managed && <Badge tone="success" appearance="subtle" size="sm">managed</Badge>}
                        {g.status && <Badge tone="info" appearance="subtle" size="sm">{g.status}</Badge>}
                      </div>
                      <div style={optMuted}>
                        {g.flavor ? `${g.flavor}` : 'load balancer'}
                        {g.cores ? ` · ${g.cores} vCPU · ${g.memory} GB` : ''}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </RadioGroup>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button disabled={pin.isPending || !sel} onClick={submit}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
